/**
 * Tool Card Component
 *
 * Main card component that combines all tool display sub-components.
 * Uses the config-driven approach for consistent tool rendering.
 * Flat, embedded design that integrates with the page.
 */ import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { classifyToolResult, humanizeToolId } from '../../lib/tool-configs';
import { CopyJsonButton } from './CopyJsonButton';
import { ToolHeader } from './ToolHeader';
import { ToolInputSummary } from './ToolInputSummary';
import { ToolProgress } from './ToolProgress';
import { ToolResult } from './ToolResult';
export function ToolCard({ config, output, input, isRunning = false, duration, progressState, rawOutput, embedded = false }) {
    // Determine result state and message. The state is decided by the shared
    // classifier (classifyToolResult) so the flat card and the Specra
    // disclosure can never disagree; only the message text lives here.
    const getResultInfo = ()=>{
        switch(classifyToolResult(config, output, isRunning)){
            case 'running':
            case 'pending':
                return null;
            case 'error':
                return config.isErrorResult?.(output) ? {
                    state: 'error',
                    message: config.formatError(output)
                } : {
                    state: 'error',
                    message: 'Invalid tool output format'
                };
            case 'empty':
                return {
                    state: 'empty',
                    message: config.emptyText || 'No results found'
                };
            case 'success':
                return {
                    state: 'success',
                    message: config.formatSuccess(output)
                };
        }
    };
    // Display name falls back to the humanized tool id when the backend
    // catalog hasn't provided one (Phase 6: baked-in configs no longer
    // carry displayName).
    const displayName = config.displayName ?? humanizeToolId(config.id);
    // Format inputs if available - wrapped in try-catch for runtime safety
    let formattedInputs = [];
    if (input && config.formatInput) {
        try {
            formattedInputs = config.formatInput(input);
        } catch (error) {
            console.warn(`Failed to format input for tool ${displayName}:`, error);
            formattedInputs = [];
        }
    }
    const resultInfo = getResultInfo();
    // Embedded (inside the Specra disclosure) drops the muted panel + padding so
    // it doesn't nest a card inside the disclosure's own panel; the scroll cap and
    // spacing stay so long output is still contained.
    const containerClass = embedded ? 'space-y-1.5 text-muted-foreground max-h-64 overflow-y-auto scrollbar-thin' : 'space-y-1.5 rounded-md bg-muted px-3 py-2 text-muted-foreground max-h-64 overflow-y-auto scrollbar-thin';
    return /*#__PURE__*/ _jsxs("div", {
        className: containerClass,
        children: [
            !embedded && /*#__PURE__*/ _jsx(ToolHeader, {
                icon: config.icon,
                displayName: displayName,
                isRunning: isRunning,
                loadingText: config.loadingText,
                duration: duration
            }),
            formattedInputs.length > 0 && /*#__PURE__*/ _jsx(ToolInputSummary, {
                inputs: formattedInputs
            }),
            config.progressSteps && config.progressSteps.length > 0 && /*#__PURE__*/ _jsx(ToolProgress, {
                steps: config.progressSteps,
                progressState: progressState
            }),
            resultInfo && /*#__PURE__*/ _jsx(ToolResult, {
                message: resultInfo.message,
                state: resultInfo.state
            }),
            !isRunning && output !== undefined && /*#__PURE__*/ _jsx("div", {
                className: "flex justify-end",
                children: /*#__PURE__*/ _jsx(CopyJsonButton, {
                    data: rawOutput ?? output
                })
            })
        ]
    });
}
